const TRACE_KEY = "orus.bootTrace.v1";
const TRACE_LIMIT = 100;
let runSequence = 0;
let currentRunId = null;

const safeSessionStorage = () => {
  try {
    return window.sessionStorage;
  } catch (_error) {
    return null;
  }
};

const readTrace = () => {
  const storage = typeof window !== "undefined" ? safeSessionStorage() : null;
  if (!storage) return [];
  try {
    const value = JSON.parse(storage.getItem(TRACE_KEY) || "[]");
    return Array.isArray(value) ? value.slice(-TRACE_LIMIT) : [];
  } catch (_error) {
    return [];
  }
};

let trace = readTrace();

const writeTrace = () => {
  const storage = typeof window !== "undefined" ? safeSessionStorage() : null;
  if (!storage) return;
  try {
    storage.setItem(TRACE_KEY, JSON.stringify(trace.slice(-TRACE_LIMIT)));
  } catch (_error) {
    // Diagnostics must never affect application startup.
  }
};

const sanitizeMetadata = (metadata = {}) => {
  const allowed = [
    "route",
    "signedIn",
    "count",
    "scope",
    "source",
    "visibility",
    "online",
    "reason",
  ];
  return Object.fromEntries(
    allowed
      .filter((key) => Object.prototype.hasOwnProperty.call(metadata, key))
      .map((key) => [key, metadata[key]]),
  );
};

export function recordBootEvent(name, metadata = {}) {
  const event = {
    name,
    at: Date.now(),
    runId: currentRunId,
    visibility:
      typeof document !== "undefined" ? document.visibilityState : "unknown",
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    ...sanitizeMetadata(metadata),
  };
  trace = [...trace, event].slice(-TRACE_LIMIT);
  writeTrace();
  return event;
}

export function beginBootRun(metadata = {}) {
  runSequence += 1;
  currentRunId = `${Date.now().toString(36)}-${runSequence.toString(36)}`;
  recordBootEvent("navigation:start", metadata);
  return currentRunId;
}

export function beginBootStep(name, metadata = {}) {
  const startedAt = performance.now();
  const runId = currentRunId;
  const startEvent = recordBootEvent(`${name}:start`, metadata);
  let finished = false;
  return (result = {}) => {
    if (finished) return;
    finished = true;
    const previousRunId = currentRunId;
    currentRunId = runId;
    const finishEvent = recordBootEvent(`${name}:finish`, {
      ...metadata,
      ...result,
      count: Number.isFinite(result.count) ? result.count : undefined,
      reason:
        typeof result.errorCode === "string"
          ? result.errorCode
          : result.outcome || "success",
    });
    currentRunId = previousRunId;
    if (finishEvent) {
      finishEvent.durationMs = Math.round(performance.now() - startedAt);
      finishEvent.startedAt = startEvent.at;
    }
    writeTrace();
  };
}

export function afterBrowserFrame() {
  if (typeof requestAnimationFrame !== "function") return Promise.resolve();
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

export function getBootTrace() {
  return trace.map((event) => ({ ...event }));
}

export function installBootLifecycleDiagnostics() {
  if (typeof window === "undefined" || window.__UNIT_BOOT_DIAGNOSTICS__) return;
  window.__UNIT_BOOT_DIAGNOSTICS__ = true;
  window.__UNIT_BOOT_TRACE__ = getBootTrace;
  window.addEventListener("pageshow", (event) =>
    recordBootEvent("lifecycle:pageshow", { reason: event.persisted ? "bfcache" : "load" }),
  );
  window.addEventListener("pagehide", (event) =>
    recordBootEvent("lifecycle:pagehide", { reason: event.persisted ? "bfcache" : "unload" }),
  );
  window.addEventListener("online", () => recordBootEvent("lifecycle:online"));
  window.addEventListener("offline", () => recordBootEvent("lifecycle:offline"));
  document.addEventListener("visibilitychange", () =>
    recordBootEvent("lifecycle:visibility", {
      visibility: document.visibilityState,
    }),
  );
}
